import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * The native <details> toggle is prevented for the drawer/submenu controls
 * and state is driven explicitly here. This avoids race conditions where a
 * fast second tap can leave [open], scroll-lock and .menu-open out of sync.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {boolean} */
  #mainOpen = false;

  /** @type {WeakMap<HTMLDetailsElement, number>} */
  #transitionTokens = new WeakMap();

  connectedCallback() {
    super.connectedCallback();

    this.#mainOpen =
      this.refs.details.hasAttribute('open') && this.refs.details.classList.contains('menu-open');

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    removeTrapFocus();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed.
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is intended to be open.
   */
  get isOpen() {
    return this.#mainOpen;
  }

  /**
   * Get the closest details element to the event target.
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Advance the transition token for one details element.
   * Stale animation callbacks are ignored when a newer interaction occurs.
   * @param {HTMLDetailsElement} details
   * @returns {number}
   */
  #nextTransition(details) {
    const token = (this.#transitionTokens.get(details) ?? 0) + 1;
    this.#transitionTokens.set(details, token);
    return token;
  }

  /**
   * @param {HTMLDetailsElement} details
   * @param {number} token
   */
  #isCurrentTransition(details, token) {
    return this.#transitionTokens.get(details) === token;
  }

  /**
   * Toggle the main menu drawer.
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault();
    return this.isOpen ? this.close(event) : this.open(undefined, event);
  }

  /**
   * Open the closest drawer or submenu.
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    event?.preventDefault();

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');
    if (!summary) return;

    const isMainDrawer = details === this.refs.details;
    const token = this.#nextTransition(details);

    if (isMainDrawer) {
      this.#mainOpen = true;
    }

    // Explicitly control <details> instead of relying on its click default.
    // This keeps scroll-lock and visual state synchronized.
    details.open = true;
    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);

    requestAnimationFrame(() => {
      if (!this.#isCurrentTransition(details, token)) return;
      if (isMainDrawer && !this.#mainOpen) return;

      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(
        drawer || details,
        () => {
          if (!this.#isCurrentTransition(details, token)) return;
          if (!details.open || !details.classList.contains('menu-open')) return;
          trapFocus(details);
        },
        { subtree: false }
      );
    });
  }

  /**
   * Go back or close the closest submenu.
   * @param {Event} [event]
   */
  back(event) {
    event?.preventDefault();
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer.
   * @param {Event} [event]
   */
  close(event) {
    event?.preventDefault();
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open.
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;

    const isMainDrawer = details === this.refs.details;
    const token = this.#nextTransition(details);

    if (isMainDrawer) {
      this.#mainOpen = false;
    }

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        // A newer tap may have reopened this same drawer while the closing
        // animation was running. Never let an old callback close the new state.
        if (!this.#isCurrentTransition(details, token)) return;
        if (details.classList.contains('menu-open')) return;

        reset(details);

        if (isMainDrawer) {
          removeTrapFocus();

          const openDetails = this.querySelectorAll(
            'details[open]:not(accordion-custom > details)'
          );
          openDetails.forEach((element) => {
            if (element !== this.refs.details) reset(element);
          });
        } else if (this.#mainOpen && this.refs.details.open) {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation.
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions
   * when the drawer opens.
   * @param {HTMLDetailsElement} details
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });

    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state.
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.open = false;
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
